import { BaseLogger } from "base_logger";
import { FlightAPI } from "contexts/flight_api_context";
import { ForumAPI } from "contexts/forum_api_context";
import { AppDispatch } from "pubsub/client";
import { Settings } from "settings";
import { setUserPresenceData } from "shared/reducers/classroom";

const PRESENCE_START_KEY = "presence-start";
const PRESENCE_END_KEY = "presence-end";

export class PresenceController extends BaseLogger {
  private classUserId: string;

  private flightAPI: FlightAPI;

  private dispatch: AppDispatch;

  private forumAPI: ForumAPI;

  constructor(
    classUserId: string,
    flightAPI: FlightAPI,
    dispatch: AppDispatch,
    forumAPI: ForumAPI,
  ) {
    super();
    this.classUserId = classUserId;
    this.flightAPI = flightAPI;
    this.dispatch = dispatch;
    this.forumAPI = forumAPI;
    this.updatePresence();
  }

  async updatePresence() {
    // Admins will not have a classUserId. Their presence is not tracked.
    if (!this.classUserId) {
      PresenceController.log("No classUserId. Skipping presence update.");
      return;
    }

    const currentTime = this.flightAPI.getServerMilliseconds();

    if (!currentTime) {
      PresenceController.log("Server time is not available yet. Skipping presence update.");
      return;
    }

    PresenceController.log(
      `Updating presence for classUser ${this.classUserId} at ${new Date(currentTime)}.`,
    );

    let presenceStart = Number(localStorage.getItem(PRESENCE_START_KEY));
    const lastPresenceEnd = Number(localStorage.getItem(PRESENCE_END_KEY));

    // Tracking a bug where currentTime is incorrect because it seems to be coming from a previous reload.
    // https://app.asana.com/0/1203570858050133/1209533308346545
    if (currentTime < lastPresenceEnd) {
      PresenceController.error(
        `currentTime ${new Date(currentTime)} is less than lastPresenceEnd ${new Date(
          lastPresenceEnd,
        )}.`,
      );
    }

    // If this is the first iteration, or if it's not the first iteration
    // but there's been too long of a gap since the last iteration for the
    // user to still be considered present, start a new presence interval.
    const maxMillis = Settings.MAX_FRONTEND_GAP_TO_BE_PRESENT_IN_SECONDS * 1000;
    if (!lastPresenceEnd || currentTime - lastPresenceEnd > maxMillis) {
      PresenceController.log("Starting new presence interval.");
      presenceStart = currentTime;
    }

    // Store timestamp values for next iteration.
    localStorage.setItem(PRESENCE_START_KEY, `${presenceStart}`);
    localStorage.setItem(PRESENCE_END_KEY, `${currentTime}`);

    // Post timestamps to the server and receive back a dictionary of
    // presence flags for each ClassUser in the class, which will be
    // used to update the 'arrival-time' and 'is-present' attributes
    // of the corresponding client-side ClassUser models.
    const data = {
      presence_from_datetime: new Date(presenceStart),
      presence_to_datetime: new Date(currentTime),
      flight: true,
    };
    PresenceController.log(
      `Sending presence data to Forum for classUser ${this.classUserId}: from "${data.presence_from_datetime}" to "${data.presence_to_datetime}".`,
    );
    const response = await this.forumAPI.post(
      `api/v1/class-users/${this.classUserId}/presence_check_in`,
      JSON.stringify(data),
      false,
      3,
    );
    if (response.ok) {
      const { user_id, seconds_present, enter_class_time } = await response.json();
      this.dispatch(
        setUserPresenceData({
          userId: `${user_id}`,
          secondsPresent: seconds_present,
          enterClassTime: enter_class_time,
        }),
      );
    }
  }
}
